import { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { authorizedAxios } from "../axios";

export type ReportBundleStatus =
  | "idle"
  | "queued"
  | "running"
  | "done"
  | "error";

/**
 * Streams one server-rendered report PDF (`uk-computation`,
 * `uk-income-statement`, `us-8949`, `us-income-statement`) for `year` and saves
 * it under the filename the server puts in `content-disposition`.
 */
export const downloadServerPdf = async (
  getToken: () => Promise<string>,
  doc: string,
  year: number
): Promise<void> => {
  const token = await getToken();
  let response;
  try {
    response = await authorizedAxios(token).get(
      `/report-jobs/pdf/${doc}?year=${year}`,
      { responseType: "blob" }
    );
  } catch (requestError: any) {
    // Errors come back as JSON, but responseType blob wraps them in a Blob.
    const data = requestError?.response?.data;
    let message = "";
    if (data && typeof data.text === "function") {
      try {
        message = JSON.parse(await data.text())?.message ?? "";
      } catch {
        // Non-JSON body — fall through to the generic message.
      }
    } else {
      message = data?.message ?? "";
    }
    throw new Error(message || "could not download pdf");
  }

  const disposition = String(response.headers["content-disposition"] ?? "");
  const match = disposition.match(/filename="([^"]+)"/);
  if (!match) {
    console.warn(
      `[useReportBundle] ${doc} ${year}: no filename in content-disposition — using generic fallback name ${doc}-${year}.pdf`
    );
  }
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = match?.[1] ?? `${doc}-${year}.pdf`;
  // Attach before clicking: the Playwright deliverables capture waits on
  // download events from this button, and attached anchors always fire them.
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const useReportBundle = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [status, setStatus] = useState<ReportBundleStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const jobIdRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  useEffect(() => stopPolling, []);

  const downloadZip = async (jobId: number) => {
    const token = await getAccessTokenSilently();
    const response = await authorizedAxios(token).get(
      `/report-jobs/${jobId}/download`,
      { responseType: "blob" }
    );
    const disposition = String(
      response.headers["content-disposition"] ?? ""
    );
    const match = disposition.match(/filename="([^"]+)"/);
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = match?.[1] ?? "moonscape-tax-reports.zip";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const generate = async (params: {
    jurisdiction: "uk" | "us";
    years: string[];
  }): Promise<void> => {
    try {
      stopPolling();
      setError(null);
      setStatus("queued");

      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).post(
        "/report-jobs",
        params
      );
      jobIdRef.current = response.data.id;

      timerRef.current = setInterval(async () => {
        try {
          const jobId = jobIdRef.current;
          if (!jobId) return;
          const pollToken = await getAccessTokenSilently();
          const job = await authorizedAxios(pollToken).get(
            `/report-jobs/${jobId}`
          );

          if (job.data.status === "done") {
            stopPolling();
            await downloadZip(jobId);
            setStatus("done");
          } else if (job.data.status === "error") {
            stopPolling();
            setError(job.data.error || "generation failed");
            setStatus("error");
          } else {
            setStatus(job.data.status);
          }
        } catch {
          // Keep polling after a transient network or token refresh failure.
        }
      }, 5_000);
    } catch (requestError: any) {
      stopPolling();
      setError(
        requestError?.response?.data?.message || "could not start generation"
      );
      setStatus("error");
    }
  };

  return { generate, status, error };
};
